// frontend/src/pages/CRManagement/CreateCRPage.js
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
    Card, Form, Input, Select, Button, DatePicker, InputNumber,
    Row, Col, Typography, Table, Tag, Popconfirm, Spin, Alert, Space, Divider
} from 'antd';
import {
    PlusOutlined, RollbackOutlined, DeleteOutlined, EditOutlined,
    FileTextOutlined, LinkOutlined, ClockCircleOutlined, SaveOutlined,
    InfoCircleOutlined, CloseOutlined, CheckOutlined, UserOutlined
} from '@ant-design/icons';
import dayjs from 'dayjs';
import api from '../../utils/api';
import { useNotificationApi } from '../../context/NotificationContext';
import { createCR, getAvailableTickets } from '../../utils/crService';
import './CRForm.css';

const { Title, Text } = Typography;
const { Option } = Select;
const { TextArea } = Input;

const DATE_FORMAT = 'DD/MM/YYYY';

// ============================================================
// CONSTANTS
// ============================================================

const ACTIVITY_TYPES = [
    'Analysis',
    'Bug Fixes',
    'Data Patching',
    'Deployment',
    'Development',
    'Documentation',
    'Final Acceptance Test',
    'Infra Setup & Configuration',
    'Meeting',
    'Patch Upgrade',
    'Review',
    'Security Hardening',
    'System Integration Test',
    'Support',
    'Testing',
    'User Acceptance Test',
    'URS',
    'Other'
];

const HOURS = Array.from({ length: 12 }, (_, i) => i + 1);
const MINUTES = Array.from({ length: 12 }, (_, i) => i * 5);
const AMPM = ['AM', 'PM'];

// ============================================================
// HELPERS
// ============================================================

const buildTimeString = (hour, minute, ampm) => {
    if (hour == null || minute == null || !ampm) return null;
    let h = parseInt(hour);
    if (ampm === 'PM' && h !== 12) h += 12;
    if (ampm === 'AM' && h === 12) h = 0;
    return `${String(h).padStart(2, '0')}:${String(minute).padStart(2, '0')}:00`;
};

const emptyEntryForm = () => ({
    entryDate: null,
    activityType: undefined,
    startTime: { hour: undefined, minute: undefined, ampm: undefined },
    endTime: { hour: undefined, minute: undefined, ampm: undefined },
    duration: null,
    resources: [],
    description: ''
});

// ============================================================
// COMPONENT
// ============================================================

const CreateCRPage = () => {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const notificationApi = useNotificationApi();
    const [form] = Form.useForm();
    const [entryForm] = Form.useForm();

    const prefillTicketId = searchParams.get('ticketId');

    const [submitting, setSubmitting] = useState(false);

    // Contracts
    const [contracts, setContracts] = useState([]);
    const [contractsLoading, setContractsLoading] = useState(false);
    const selectedContract = Form.useWatch('contractId', form);

    // Ticket types (used for CR Service Category)
    const [ticketTypes, setTicketTypes] = useState([]);
    const [ticketTypesLoading, setTicketTypesLoading] = useState(false);

    // Tickets
    const [availableTickets, setAvailableTickets] = useState([]);
    const [ticketsLoading, setTicketsLoading] = useState(false);
    const [linkedTicketIds, setLinkedTicketIds] = useState([]);

    // Users
    const [users, setUsers] = useState([]);
    const [usersLoading, setUsersLoading] = useState(false);

    // Time entries
    const [timeEntries, setTimeEntries] = useState([]);
    const [editingEntryKey, setEditingEntryKey] = useState(null);

    // Prefill banner
    const [prefillContext, setPrefillContext] = useState(null);

    // Watch entry form for duration calculation
    const eStartHour = Form.useWatch('startHour', entryForm);
    const eStartMinute = Form.useWatch('startMinute', entryForm);
    const eStartAmPm = Form.useWatch('startAmPm', entryForm);
    const eEndHour = Form.useWatch('endHour', entryForm);
    const eEndMinute = Form.useWatch('endMinute', entryForm);
    const eEndAmPm = Form.useWatch('endAmPm', entryForm);

    // Notifications
    const showError = useCallback((title, content) => {
        notificationApi.error({
            message: title, description: content,
            duration: 4.5, placement: 'topRight', style: { width: 420 }
        });
    }, [notificationApi]);

    const showSuccess = useCallback((title, content) => {
        notificationApi.success({
            message: title, description: content,
            duration: 3, placement: 'topRight', style: { width: 420 }
        });
    }, [notificationApi]);

    // ------------------------------------------------------------
    // Load contracts
    // ------------------------------------------------------------
    useEffect(() => {
        const fetchContracts = async () => {
            setContractsLoading(true);
            try {
                const res = await api.get('/api/contracts');
                if (res.data.success) setContracts(res.data.data || []);
            } catch (err) {
                console.error('[CreateCRPage] Contracts fetch failed:', err);
                showError('Failed to Load', 'Could not load contracts.');
            } finally {
                setContractsLoading(false);
            }
        };
        fetchContracts();
    }, [showError]);

    // ------------------------------------------------------------
    // Load ticket types (for Service Category)
    // ------------------------------------------------------------
    useEffect(() => {
        const fetchTicketTypes = async () => {
            setTicketTypesLoading(true);
            try {
                const res = await api.get('/api/ticket-types');
                const list = res.data?.data || res.data || [];
                setTicketTypes(list);
            } catch (err) {
                console.error('[CreateCRPage] Ticket types fetch failed:', err);
                showError('Failed to Load', 'Could not load service categories.');
            } finally {
                setTicketTypesLoading(false);
            }
        };
        fetchTicketTypes();
    }, [showError]);

    // ------------------------------------------------------------
    // Load users
    // ------------------------------------------------------------
    useEffect(() => {
        const fetchUsers = async () => {
            setUsersLoading(true);
            try {
                const res = await api.get('/api/users/active-internal-and-partner');
                if (res.data.success) {
                    const filtered = (res.data.data || []).filter(u => u.userType !== 'customer');
                    setUsers(filtered);
                }
            } catch (err) {
                console.error('[CreateCRPage] Users fetch failed:', err);
            } finally {
                setUsersLoading(false);
            }
        };
        fetchUsers();
    }, []);

    // ------------------------------------------------------------
    // Tickets when contract changes
    // ------------------------------------------------------------
    useEffect(() => {
        const fetchTickets = async () => {
            if (!selectedContract) {
                setAvailableTickets([]);
                setLinkedTicketIds([]);
                return;
            }
            setTicketsLoading(true);
            try {
                const res = await getAvailableTickets(selectedContract);
                if (res.success) setAvailableTickets(res.data || []);
            } catch (err) {
                console.error('[CreateCRPage] Tickets fetch failed:', err);
                showError('Failed to Load', 'Could not load tickets for the selected contract.');
            } finally {
                setTicketsLoading(false);
            }
        };
        fetchTickets();
    }, [selectedContract, showError]);

    // ------------------------------------------------------------
    // Prefill from ?ticketId=X
    // ------------------------------------------------------------
    useEffect(() => {
        const doPrefill = async () => {
            if (!prefillTicketId || contracts.length === 0) return;
            try {
                const res = await api.get(`/api/tickets/${prefillTicketId}`);
                if (res.data.success) {
                    const t = res.data.data;
                    const contractId = t.contractId || t.contract_id;
                    const ticketTypeId = t.ticketTypeId || t.ticket_type_id;

                    form.setFieldsValue({
                        contractId,
                        // Pre-select the Service Category from the ticket's type
                        crTypeId: ticketTypeId || undefined
                    });
                    setLinkedTicketIds([parseInt(prefillTicketId)]);
                    setPrefillContext({
                        ticketId: prefillTicketId,
                        ticketUuid: t.ticketUuid || t.ticket_uuid,
                        ticketTitle: t.title,
                        contractId
                    });
                }
            } catch (err) {
                console.error('[CreateCRPage] Prefill failed:', err);
            }
        };
        doPrefill();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [prefillTicketId, contracts.length, ticketTypes.length]);

    // ------------------------------------------------------------
    // Ticket options
    // ------------------------------------------------------------
    const ticketOptions = useMemo(() => {
        return availableTickets.map(t => ({ value: t.id, ticket: t }));
    }, [availableTickets]);

    // ------------------------------------------------------------
    // Auto-calculate duration in the entry form
    // ------------------------------------------------------------
    useEffect(() => {
        const allStart = eStartHour != null && eStartMinute != null && eStartAmPm;
        const allEnd = eEndHour != null && eEndMinute != null && eEndAmPm;

        if (allStart && allEnd) {
            let sh = parseInt(eStartHour);
            let eh = parseInt(eEndHour);
            if (eStartAmPm === 'PM' && sh !== 12) sh += 12;
            if (eStartAmPm === 'AM' && sh === 12) sh = 0;
            if (eEndAmPm === 'PM' && eh !== 12) eh += 12;
            if (eEndAmPm === 'AM' && eh === 12) eh = 0;

            const diff = (eh * 60 + parseInt(eEndMinute)) - (sh * 60 + parseInt(eStartMinute));
            if (diff > 0) {
                const h = Math.floor(diff / 60);
                const m = diff % 60;
                entryForm.setFieldsValue({ duration: parseFloat((h + m / 60).toFixed(2)) });
            } else {
                entryForm.setFieldsValue({ duration: null });
            }
        } else {
            entryForm.setFieldsValue({ duration: null });
        }
    }, [eStartHour, eStartMinute, eStartAmPm, eEndHour, eEndMinute, eEndAmPm, entryForm]);

    // ------------------------------------------------------------
    // Add / Update entry
    // ------------------------------------------------------------
    const handleAddOrUpdateEntry = async () => {
        let values;
        try {
            values = await entryForm.validateFields();
        } catch {
            return;
        }

        const entry = {
            key: editingEntryKey || `new-${Date.now()}-${Math.random()}`,
            entryDate: values.entryDate,
            activityType: values.activityType,
            startTime: {
                hour: values.startHour,
                minute: values.startMinute,
                ampm: values.startAmPm
            },
            endTime: {
                hour: values.endHour,
                minute: values.endMinute,
                ampm: values.endAmPm
            },
            duration: values.duration,
            resources: values.resources || [],
            description: values.description || ''
        };

        if (editingEntryKey) {
            setTimeEntries(prev => prev.map(e => (e.key === editingEntryKey ? entry : e)));
            setEditingEntryKey(null);
        } else {
            setTimeEntries(prev => [...prev, entry]);
        }

        entryForm.resetFields();
        entryForm.setFieldsValue(emptyEntryForm());
    };

    const handleEditEntry = (record) => {
        setEditingEntryKey(record.key);
        entryForm.setFieldsValue({
            entryDate: record.entryDate,
            activityType: record.activityType,
            startHour: record.startTime?.hour,
            startMinute: record.startTime?.minute,
            startAmPm: record.startTime?.ampm,
            endHour: record.endTime?.hour,
            endMinute: record.endTime?.minute,
            endAmPm: record.endTime?.ampm,
            duration: record.duration,
            resources: record.resources || [],
            description: record.description || ''
        });
        // Scroll to the form
        document.querySelector('.ccr-entry-form-card')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    };

    const handleCancelEdit = () => {
        setEditingEntryKey(null);
        entryForm.resetFields();
        entryForm.setFieldsValue(emptyEntryForm());
    };

    const handleRemoveEntry = (key) => {
        setTimeEntries(prev => prev.filter(e => e.key !== key));
        if (editingEntryKey === key) handleCancelEdit();
    };

    // ------------------------------------------------------------
    // Totals
    // ------------------------------------------------------------
    const totals = useMemo(() => {
        let hours = 0;
        timeEntries.forEach(e => { hours += parseFloat(e.duration || 0); });
        return {
            totalHours: parseFloat(hours.toFixed(2)),
            totalManDays: parseFloat((hours / 8).toFixed(2))
        };
    }, [timeEntries]);

    // ------------------------------------------------------------
    // Display table columns (read-only list)
    // ------------------------------------------------------------
    const entryColumns = useMemo(() => [
        {
            title: 'Date',
            dataIndex: 'entryDate',
            key: 'entryDate',
            width: 120,
            render: (d) => d ? dayjs(d).format('DD MMM YYYY') : '—'
        },
        {
            title: 'Activity Type',
            dataIndex: 'activityType',
            key: 'activityType',
            width: 170,
            render: (t) => t ? <Tag color="cyan">{t}</Tag> : '—'
        },
        {
            title: 'Start',
            dataIndex: 'startTime',
            key: 'startTime',
            width: 100,
            render: (t) => {
                const s = buildTimeString(t?.hour, t?.minute, t?.ampm);
                return s ? dayjs(s, 'HH:mm:ss').format('hh:mm A') : '—';
            }
        },
        {
            title: 'End',
            dataIndex: 'endTime',
            key: 'endTime',
            width: 100,
            render: (t) => {
                const s = buildTimeString(t?.hour, t?.minute, t?.ampm);
                return s ? dayjs(s, 'HH:mm:ss').format('hh:mm A') : '—';
            }
        },
        {
            title: 'Duration',
            dataIndex: 'duration',
            key: 'duration',
            width: 100,
            align: 'right',
            render: (v) => v != null ? <Text strong>{parseFloat(v).toFixed(2)} hrs</Text> : '—'
        },
        {
            title: 'Resource(s)',
            dataIndex: 'resources',
            key: 'resources',
            render: (rs) => {
                if (!rs || rs.length === 0) return '—';
                return (
                    <Space size={4} wrap>
                        {rs.slice(0, 2).map((r, i) => (
                            <Tag key={i} color="blue">{r}</Tag>
                        ))}
                        {rs.length > 2 && <Tag>+{rs.length - 2}</Tag>}
                    </Space>
                );
            }
        },
        {
            title: 'Description',
            dataIndex: 'description',
            key: 'description',
            ellipsis: true,
            render: (t) => t || '—'
        },
        {
            title: '',
            key: 'actions',
            width: 90,
            align: 'center',
            fixed: 'right',
            render: (_, record) => (
                <Space size={2}>
                    <Button
                        type="text"
                        size="small"
                        icon={<EditOutlined />}
                        onClick={() => handleEditEntry(record)}
                    />
                    <Popconfirm
                        title="Remove this entry?"
                        onConfirm={() => handleRemoveEntry(record.key)}
                        okText="Remove"
                        cancelText="Cancel"
                        okButtonProps={{ danger: true }}
                    >
                        <Button type="text" danger size="small" icon={<DeleteOutlined />} />
                    </Popconfirm>
                </Space>
            )
        }
    ], [editingEntryKey]);

    // ------------------------------------------------------------
    // Submit
    // ------------------------------------------------------------
    const handleSubmit = async () => {
        let values;
        try {
            values = await form.validateFields();
        } catch {
            showError('Missing Fields', 'Please fill in all required fields.');
            return;
        }

        const cleanedEntries = timeEntries.map(e => ({
            entryDate: e.entryDate.format('YYYY-MM-DD'),
            startTime: buildTimeString(e.startTime?.hour, e.startTime?.minute, e.startTime?.ampm),
            endTime: buildTimeString(e.endTime?.hour, e.endTime?.minute, e.endTime?.ampm),
            duration: parseFloat(e.duration),
            activityType: e.activityType,
            resource: (e.resources || []).join(', '),
            description: e.description || null
        }));

        const payload = {
            contractId: parseInt(values.contractId),
            crTypeId: parseInt(values.crTypeId),
            title: values.title?.trim(),
            requestDate: values.requestDate.format('YYYY-MM-DD'),
            description: values.description?.trim(),
            targetProductionDate: values.targetProductionDate
                ? values.targetProductionDate.format('YYYY-MM-DD')
                : null,
            estimatedMandays: values.estimatedMandays != null ? values.estimatedMandays : null,
            impactProgress: values.impactProgress?.trim() || null,
            userStoryNo: values.userStoryNo?.trim() || null,
            linkedTicketIds: linkedTicketIds,
            timeEntries: cleanedEntries
        };

        setSubmitting(true);
        try {
            const res = await createCR(payload);
            if (res.success) {
                showSuccess('CR Created', `CR ${res.data.crNumber} created successfully.`);
                setTimeout(() => navigate(`/cr-management/${res.data.id}`), 600);
            } else {
                showError('Create Failed', res.message || 'Failed to create CR.');
            }
        } catch (err) {
            console.error('[CreateCRPage] Submit error:', err);
            showError('Create Failed', err.response?.data?.message || 'An error occurred.');
        } finally {
            setSubmitting(false);
        }
    };

    // ------------------------------------------------------------
    // Render
    // ------------------------------------------------------------
    return (
        <div className="ccr-page">
            <div className="ccr-header">
                <div className="ccr-header-left">
                    <Title level={2} className="ccr-title">
                        <PlusOutlined /> Create Change Request
                    </Title>
                    <Text type="secondary" className="ccr-subtitle">
                        Register a new CR, link related tickets, and optionally log initial time entries.
                    </Text>
                </div>
                <Button
                    icon={<RollbackOutlined />}
                    size="large"
                    className="ccr-back-btn"
                    onClick={() => navigate('/cr-management')}
                >
                    Back to CR List
                </Button>
            </div>

            {prefillContext && (
                <Alert
                    className="ccr-prefill-banner"
                    type="info" showIcon icon={<InfoCircleOutlined />}
                    message={<span>Opened from ticket <strong>{prefillContext.ticketUuid}</strong>{prefillContext.ticketTitle && ` — ${prefillContext.ticketTitle}`}</span>}
                    description="Contract and linked ticket have been pre-filled. You can still change them."
                />
            )}

            <Form form={form} layout="vertical" name="create_cr_form" autoComplete="off">
                {/* SECTION 1 — DETAILS */}
                <Card className="ccr-section-card" bordered={false}>
                    <div className="ccr-section-header">
                        <FileTextOutlined className="ccr-section-icon" />
                        <div>
                            <div className="ccr-section-title">CR Details</div>
                            <div className="ccr-section-hint">Basic information about this Change Request</div>
                        </div>
                    </div>

                    {/* Row 1: Contract + Service Category */}
                    <Row gutter={20}>
                        <Col xs={24} md={12}>
                            <Form.Item
                                name="contractId"
                                label="Contract"
                                rules={[{ required: true, message: 'Please select a contract' }]}
                            >
                                <Select
                                    placeholder="Select contract"
                                    showSearch
                                    optionFilterProp="children"
                                    loading={contractsLoading}
                                    size="large"
                                >
                                    {contracts.map(c => (
                                        <Option key={c.id} value={c.id}>
                                            {c.contractName || `Contract #${c.id}`}
                                        </Option>
                                    ))}
                                </Select>
                            </Form.Item>
                        </Col>
                        <Col xs={24} md={12}>
                            <Form.Item
                                name="crTypeId"
                                label="Service Category"
                                rules={[{ required: true, message: 'Please select a service category' }]}
                                tooltip="Which type of service does this CR represent?"
                            >
                                <Select
                                    placeholder="Select service category"
                                    showSearch
                                    optionFilterProp="children"
                                    loading={ticketTypesLoading}
                                    size="large"
                                >
                                    {ticketTypes.map(tt => (
                                        <Option key={tt.id} value={tt.id}>
                                            {tt.name}
                                        </Option>
                                    ))}
                                </Select>
                            </Form.Item>
                        </Col>
                    </Row>

                    {/* Row 2: Request Date */}
                    <Row gutter={20}>
                        <Col xs={24} md={12}>
                            <Form.Item
                                name="requestDate"
                                label="Request Date"
                                rules={[{ required: true, message: 'Please select a request date' }]}
                            >
                                <DatePicker
                                    style={{ width: '100%' }}
                                    format={DATE_FORMAT}
                                    size="large"
                                    placeholder="Select date"
                                />
                            </Form.Item>
                        </Col>
                    </Row>

                    <Form.Item
                        name="title" label="Title"
                        rules={[
                            { required: true, message: 'Please enter a title' },
                            { max: 255, message: 'Maximum 255 characters' }
                        ]}
                    >
                        <Input
                            placeholder="e.g., Notis Pink 2.0 — Add TARIKH_DOKUMEN field"
                            size="large" maxLength={255} showCount
                        />
                    </Form.Item>

                    <Form.Item
                        name="description" label="Description"
                        rules={[{ required: true, message: 'Please enter a description' }]}
                    >
                        <TextArea rows={4} placeholder="Describe the change request..." maxLength={2000} showCount />
                    </Form.Item>

                    <Row gutter={20}>
                        <Col xs={24} md={12}>
                            <Form.Item name="targetProductionDate" label="Target Production Date">
                                <DatePicker style={{ width: '100%' }} format={DATE_FORMAT} size="large" placeholder="Select date" />
                            </Form.Item>
                        </Col>
                        <Col xs={24} md={12}>
                            <Form.Item name="estimatedMandays" label="Estimated Man-Days">
                                <InputNumber
                                    style={{ width: '100%' }} min={0} step={0.5}
                                    size="large" placeholder="e.g., 15.5"
                                />
                            </Form.Item>
                        </Col>
                    </Row>

                    <Row gutter={20}>
                        <Col xs={24} md={12}>
                            <Form.Item name="userStoryNo" label="User Story No.">
                                <Input placeholder="e.g., US-1234" size="large" maxLength={100} />
                            </Form.Item>
                        </Col>
                        <Col xs={24} md={12}>
                            <Form.Item name="impactProgress" label="Impact / Progress">
                                <TextArea rows={3} placeholder="e.g., URS received, In progress..." maxLength={500} />
                            </Form.Item>
                        </Col>
                    </Row>
                </Card>

                {/* SECTION 2 — LINKED TICKETS */}
                <Card className="ccr-section-card" bordered={false}>
                    <div className="ccr-section-header">
                        <LinkOutlined className="ccr-section-icon" />
                        <div>
                            <div className="ccr-section-title">Linked Tickets</div>
                            <div className="ccr-section-hint">Optional. Link one or more tickets that are part of this CR.</div>
                        </div>
                    </div>

                    {!selectedContract ? (
                        <Alert
                            type="warning" showIcon
                            message="Select a contract first"
                            description="The ticket picker is scoped to the selected contract."
                        />
                    ) : (
                        <>
                            <Select
                                mode="multiple"
                                placeholder="Search and select tickets..."
                                style={{ width: '100%' }}
                                size="large"
                                loading={ticketsLoading}
                                value={linkedTicketIds}
                                onChange={setLinkedTicketIds}
                                showSearch
                                filterOption={(input, option) => {
                                    const t = ticketOptions.find(o => o.value === option.value)?.ticket;
                                    if (!t) return false;
                                    const s = input.toLowerCase();
                                    return (
                                        (t.ticketUuid || '').toLowerCase().includes(s) ||
                                        (t.title || '').toLowerCase().includes(s) ||
                                        (t.ticketTypeName || '').toLowerCase().includes(s)
                                    );
                                }}
                                maxTagCount="responsive"
                                notFoundContent={ticketsLoading ? <Spin size="small" /> : 'No tickets found'}
                            >
                                {ticketOptions.map(opt => (
                                    <Option key={opt.value} value={opt.value}>
                                        <div className="ccr-ticket-option">
                                            <span className="ccr-ticket-option-uuid">{opt.ticket.ticketUuid}</span>
                                            {opt.ticket.ticketTypeName && (
                                                <Tag color="blue" className="ccr-ticket-option-type">
                                                    {opt.ticket.ticketTypeName}
                                                </Tag>
                                            )}
                                            <span className="ccr-ticket-option-title">{opt.ticket.title}</span>
                                        </div>
                                    </Option>
                                ))}
                            </Select>

                            {linkedTicketIds.length > 0 && (
                                <div className="ccr-linked-tickets-summary">
                                    <Text type="secondary" style={{ fontSize: 13 }}>
                                        {linkedTicketIds.length} ticket{linkedTicketIds.length === 1 ? '' : 's'} linked
                                    </Text>
                                </div>
                            )}
                        </>
                    )}
                </Card>

                {/* SECTION 3 — TIME ENTRIES */}
                <Card className="ccr-section-card" bordered={false}>
                    <div className="ccr-section-header">
                        <ClockCircleOutlined className="ccr-section-icon" />
                        <div>
                            <div className="ccr-section-title">Time Entries</div>
                            <div className="ccr-section-hint">
                                Optional. Log time entries against this CR. You can add more later.
                            </div>
                        </div>
                    </div>

                    {/* -------- ADD / EDIT FORM -------- */}
                    <Card
                        className={`ccr-entry-form-card ${editingEntryKey ? 'ccr-entry-form-editing' : ''}`}
                        bordered={false}
                    >
                        <div className="ccr-entry-form-title">
                            {editingEntryKey ? (
                                <><EditOutlined /> Editing entry</>
                            ) : (
                                <><PlusOutlined /> Add Time Entry</>
                            )}
                        </div>

                        <Form
                            form={entryForm}
                            layout="vertical"
                            initialValues={emptyEntryForm()}
                        >
                            <Row gutter={16}>
                                <Col xs={24} md={8}>
                                    <Form.Item
                                        name="entryDate" label="Date"
                                        rules={[{ required: true, message: 'Date is required' }]}
                                    >
                                        <DatePicker style={{ width: '100%' }} format={DATE_FORMAT} placeholder="Select date" />
                                    </Form.Item>
                                </Col>
                                <Col xs={24} md={8}>
                                    <Form.Item
                                        name="activityType" label="Activity Type"
                                        rules={[{ required: true, message: 'Activity type is required' }]}
                                    >
                                        <Select placeholder="Select activity type" showSearch optionFilterProp="children">
                                            {ACTIVITY_TYPES.map(t => <Option key={t} value={t}>{t}</Option>)}
                                        </Select>
                                    </Form.Item>
                                </Col>
                                <Col xs={24} md={8}>
                                    <Form.Item
                                        name="resources" label="Resource(s)"
                                        rules={[{ required: true, message: 'Select at least one resource' }]}
                                    >
                                        <Select
                                            mode="multiple"
                                            placeholder="Select one or more"
                                            loading={usersLoading}
                                            maxTagCount="responsive"
                                            showSearch
                                            filterOption={(input, option) =>
                                                (option?.children || '').toString().toLowerCase().includes(input.toLowerCase())
                                            }
                                        >
                                            {users.map(u => (
                                                <Option key={u.id} value={`${u.firstName} ${u.lastName}`}>
                                                    {u.firstName} {u.lastName}
                                                </Option>
                                            ))}
                                        </Select>
                                    </Form.Item>
                                </Col>
                            </Row>

                            <Row gutter={16}>
                                <Col xs={24} md={8}>
                                    <Form.Item label="Start Time" required style={{ marginBottom: 0 }}>
                                        <div className="ccr-time-picker">
                                            <Form.Item name="startHour" noStyle rules={[{ required: true, message: 'HH' }]}>
                                                <Select placeholder="HH" style={{ width: 68 }}>
                                                    {HOURS.map(h => <Option key={h} value={h}>{String(h).padStart(2, '0')}</Option>)}
                                                </Select>
                                            </Form.Item>
                                            <span className="ccr-time-sep">:</span>
                                            <Form.Item name="startMinute" noStyle rules={[{ required: true, message: 'MM' }]}>
                                                <Select placeholder="MM" style={{ width: 68 }}>
                                                    {MINUTES.map(m => <Option key={m} value={m}>{String(m).padStart(2, '0')}</Option>)}
                                                </Select>
                                            </Form.Item>
                                            <Form.Item name="startAmPm" noStyle rules={[{ required: true, message: 'AM/PM' }]}>
                                                <Select placeholder="AM/PM" style={{ width: 85 }}>
                                                    {AMPM.map(p => <Option key={p} value={p}>{p}</Option>)}
                                                </Select>
                                            </Form.Item>
                                        </div>
                                    </Form.Item>
                                    <Form.Item
                                        name="startHour" hidden
                                        rules={[{ required: true, message: 'Start time is required' }]}
                                    >
                                        <Input />
                                    </Form.Item>
                                </Col>
                                <Col xs={24} md={8}>
                                    <Form.Item label="End Time" required style={{ marginBottom: 0 }}>
                                        <div className="ccr-time-picker">
                                            <Form.Item name="endHour" noStyle rules={[{ required: true, message: 'HH' }]}>
                                                <Select placeholder="HH" style={{ width: 68 }}>
                                                    {HOURS.map(h => <Option key={h} value={h}>{String(h).padStart(2, '0')}</Option>)}
                                                </Select>
                                            </Form.Item>
                                            <span className="ccr-time-sep">:</span>
                                            <Form.Item name="endMinute" noStyle rules={[{ required: true, message: 'MM' }]}>
                                                <Select placeholder="MM" style={{ width: 68 }}>
                                                    {MINUTES.map(m => <Option key={m} value={m}>{String(m).padStart(2, '0')}</Option>)}
                                                </Select>
                                            </Form.Item>
                                            <Form.Item name="endAmPm" noStyle rules={[{ required: true, message: 'AM/PM' }]}>
                                                <Select placeholder="AM/PM" style={{ width: 85 }}>
                                                    {AMPM.map(p => <Option key={p} value={p}>{p}</Option>)}
                                                </Select>
                                            </Form.Item>
                                        </div>
                                    </Form.Item>
                                    <Form.Item
                                        name="endHour" hidden
                                        rules={[{ required: true, message: 'End time is required' }]}
                                    >
                                        <Input />
                                    </Form.Item>
                                </Col>
                                <Col xs={24} md={8}>
                                    <Form.Item
                                        name="duration" label="Duration (hours)"
                                        rules={[{ required: true, message: 'Duration is required' }]}
                                    >
                                        <InputNumber
                                            style={{ width: '100%' }}
                                            disabled
                                            placeholder="Auto-calculated"
                                        />
                                    </Form.Item>
                                </Col>
                            </Row>

                            <Form.Item name="description" label="Description">
                                <TextArea rows={2} placeholder="What was done..." maxLength={500} />
                            </Form.Item>

                            <div className="ccr-entry-form-actions">
                                {editingEntryKey && (
                                    <Button
                                        icon={<CloseOutlined />}
                                        onClick={handleCancelEdit}
                                    >
                                        Cancel Edit
                                    </Button>
                                )}
                                <Button
                                    type="primary"
                                    icon={editingEntryKey ? <CheckOutlined /> : <PlusOutlined />}
                                    onClick={handleAddOrUpdateEntry}
                                >
                                    {editingEntryKey ? 'Update Entry' : 'Add to List'}
                                </Button>
                            </div>
                        </Form>
                    </Card>

                    {/* -------- LIST OF ADDED ENTRIES -------- */}
                    {timeEntries.length > 0 && (
                        <>
                            <Divider orientation="left" style={{ marginTop: 24 }}>
                                <Text strong style={{ fontSize: 14 }}>
                                    Added Entries ({timeEntries.length})
                                </Text>
                            </Divider>

                            <Table
                                columns={entryColumns}
                                dataSource={timeEntries}
                                rowKey="key"
                                pagination={false}
                                size="small"
                                bordered
                                scroll={{ x: 'max-content' }}
                                className="ccr-entries-table"
                            />

                            <div className="ccr-totals-panel">
                                <div className="ccr-totals-item">
                                    <span className="ccr-totals-label">Total Hours</span>
                                    <span className="ccr-totals-value ccr-totals-hours">
                                        {totals.totalHours.toFixed(2)} hrs
                                    </span>
                                </div>
                                <div className="ccr-totals-divider" />
                                <div className="ccr-totals-item">
                                    <span className="ccr-totals-label">Total Man-Days</span>
                                    <span className="ccr-totals-value ccr-totals-days">
                                        {totals.totalManDays.toFixed(2)} days
                                    </span>
                                </div>
                                <div className="ccr-totals-note">(8 hours = 1 day)</div>
                            </div>
                        </>
                    )}
                </Card>

                <div className="ccr-actions">
                    <Button
                        size="large"
                        onClick={() => navigate('/cr-management')}
                        disabled={submitting}
                        className="ccr-cancel-btn"
                    >
                        Cancel
                    </Button>
                    <Button
                        type="primary"
                        size="large"
                        icon={<SaveOutlined />}
                        onClick={handleSubmit}
                        loading={submitting}
                        className="ccr-submit-btn"
                    >
                        Create CR
                    </Button>
                </div>
            </Form>
        </div>
    );
};

export default CreateCRPage;